import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
    Card,
    Select,
    Button,
    Table,
    Space,
    Typography,
    Tooltip,
    Alert,
    Modal,
    Row,
    Col,
    Checkbox,
    Divider,
    Tag,
} from 'antd';
import {
    ArrowLeftOutlined,
    CheckCircleOutlined,
    ReloadOutlined,
    ExclamationCircleOutlined,
    LockOutlined,
    SwapRightOutlined,
    CalendarOutlined,
    FileTextOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getRequest, postRequest } from 'api';
import { getStoredWorkMonth } from 'utils/workMonthStorage';
import { ClassLevelSelect } from 'Components';
import './ClassCarryForwardBoard.css';

const { Text } = Typography;
const { Option } = Select;

interface CancelInvoiceDetail {
    billId: string;
    incomeSchoolName: string;
    cost: number;
    collectBy: number;
    cancelReason: string;
}

interface CarryItem {
    incomeSchoolId: number;
    incomeSchoolCode: string;
    incomeSchoolName: string;
    collectBy: number;
    statusText: string;
    amount: number;
    units: number;
    isPaid: boolean;
    needsRecreateNextMonth?: boolean;
    billId?: string;
}

interface StudentCarryOverview {
    studentClassYearId: number;
    studentId: number;
    studentCode: string;
    fullName: string;
    studentName: string;
    revenueBlockName: string;
    rateItems: CarryItem[];
    monthlyItems: CarryItem[];
    annualItems: CarryItem[];
    netCarryAmount: number;
    annualRecreateAmount?: number;
    invoicesToCancelCount: number;
    invoiceIdsToCancel: string[];
    cancelInvoicesDetail?: CancelInvoiceDetail[];
}

interface CarryForwardOverviewData {
    classYearId: number;
    className: string;
    schoolYear: string;
    month: number;
    year: number;
    settleStatus: number;
    settledBy?: string;
    settledDate?: string;
    carriedDate?: string;
    totalStudents: number;
    totalExcessAmount: number;
    totalDebtAmount: number;
    totalInvoicesToCancel: number;
    totalAnnualUnpaidAmount?: number;
    totalAnnualUnpaidStudents?: number;
    students: StudentCarryOverview[];
}

interface ClassYearOption {
    classYearId: number;
    className: string;
    schoolYear: string;
    classLevel: string;
}

export const ClassCarryForwardBoard: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const schoolIdStr = localStorage.getItem('schoolId');
    const schoolId = schoolIdStr ? parseInt(schoolIdStr, 10) : null;

    const [selectedYear, setSelectedYear] = useState<dayjs.Dayjs | null>(() => dayjs(`${getStoredWorkMonth().year}`, 'YYYY'));
    const [selectedMonth, setSelectedMonth] = useState<number>(() => getStoredWorkMonth().month);
    const [selectedClassYearId, setSelectedClassYearId] = useState<number | null>(null);
    const [selectedClassLevel, setSelectedClassLevel] = useState<string | undefined>(undefined);

    const [classList, setClassList] = useState<ClassYearOption[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [carrying, setCarrying] = useState<boolean>(false);
    const [showCarryConfirm, setShowCarryConfirm] = useState<boolean>(false);
    const [copyQuickRegister, setCopyQuickRegister] = useState<boolean>(true);

    const [overviewData, setOverviewData] = useState<CarryForwardOverviewData | null>(null);

    // Lắng nghe thay đổi kỳ làm việc từ cấu hình
    useEffect(() => {
        const handleWorkMonthChange = (e: any) => {
            const { month, year } = e.detail || getStoredWorkMonth();
            setSelectedMonth(month);
            setSelectedYear(dayjs(`${year}`, 'YYYY'));
        };
        window.addEventListener('workMonthChanged', handleWorkMonthChange);
        return () => window.removeEventListener('workMonthChanged', handleWorkMonthChange);
    }, []);

    // Fetch class list
    const fetchClasses = useCallback(async (year: number) => {
        if (!schoolId) return;
        try {
            const res: any = await getRequest(
                `/cms/class/get-class-years-by-start-end-schoolId?start=${year}&schoolId=${schoolId}&page=1&perPage=200&sort=asc&sortBy=className`
            );
            if (res?.code === 200 && res.info?.list) {
                setClassList(res.info.list);
                const paramClassId = searchParams.get('classYearId');
                if (paramClassId) {
                    const id = parseInt(paramClassId, 10);
                    if (res.info.list.some((c: any) => c.classYearId === id)) {
                        setSelectedClassYearId(id);
                        return;
                    }
                }
                if (!selectedClassYearId && res.info.list.length > 0) {
                    setSelectedClassYearId(res.info.list[0].classYearId);
                }
            }
        } catch {
            toast.error('Lỗi khi tải danh sách lớp');
        }
    }, [schoolId, searchParams, selectedClassYearId]);

    useEffect(() => {
        if (selectedYear) {
            fetchClasses(selectedYear.year());
        }
    }, [selectedYear, fetchClasses]);

    const filteredClassList = useMemo(() => {
        if (!selectedClassLevel) return classList;
        return classList.filter((c) => String(c.classLevel).trim() === selectedClassLevel.trim());
    }, [classList, selectedClassLevel]);

    const handleClassLevelChange = (level?: string) => {
        setSelectedClassLevel(level);
        const nextList = level
            ? classList.filter((c) => String(c.classLevel).trim() === level.trim())
            : classList;
        if (nextList.length > 0) {
            if (!nextList.some((c) => c.classYearId === selectedClassYearId)) {
                setSelectedClassYearId(nextList[0].classYearId);
                setSearchParams({ classYearId: String(nextList[0].classYearId) });
            }
        } else {
            setSelectedClassYearId(null);
            setOverviewData(null);
        }
    };

    // Fetch overview data
    const fetchOverviewData = useCallback(async () => {
        if (!selectedClassYearId || !selectedYear) return;
        setLoading(true);
        try {
            const yearNum = selectedYear.year();
            const res: any = await getRequest(
                `/cms/accountant/class-income/carry-forward/overview?classYearId=${selectedClassYearId}&month=${selectedMonth}&year=${yearNum}`
            );
            if (res?.code === 200 && res.info) {
                setOverviewData(res.info);
            } else {
                toast.error(res?.msg || 'Không thể tải tổng quan kết chuyển');
            }
        } catch {
            toast.error('Lỗi kết nối khi tải tổng quan kết chuyển');
        } finally {
            setLoading(false);
        }
    }, [selectedClassYearId, selectedMonth, selectedYear]);

    useEffect(() => {
        fetchOverviewData();
    }, [fetchOverviewData]);

    const handleClassYearChange = (val: number) => {
        setSelectedClassYearId(val);
        setSearchParams({ classYearId: String(val) });
    };

    const nextMonth = selectedMonth === 12 ? 1 : selectedMonth + 1;
    const nextYear = selectedMonth === 12 ? (selectedYear ? selectedYear.year() + 1 : 2027) : (selectedYear ? selectedYear.year() : 2026);

    // Thực hiện kết chuyển
    const handleExecuteCarryForward = async () => {
        if (!selectedClassYearId || !selectedYear) return;
        setCarrying(true);
        try {
            const res: any = await postRequest('/cms/accountant/class-income/actual-settle/carry-forward', {
                classYearId: selectedClassYearId,
                month: selectedMonth,
                year: selectedYear.year(),
                copyQuickRegister: copyQuickRegister,
            });
            if (res?.code === 200) {
                setShowCarryConfirm(false);
                toast.success(res.info?.message || res.msg || 'Kết chuyển số dư và khóa sổ thành công!');
                await fetchOverviewData();
            } else {
                toast.error(res?.msg || 'Lỗi khi kết chuyển số dư');
            }
        } catch {
            toast.error('Lỗi kết nối khi kết chuyển số dư');
        } finally {
            setCarrying(false);
        }
    };

    // Chuẩn kế toán: Bảng đối soát tổng hợp (General Ledger Matrix)
    const columns: ColumnsType<StudentCarryOverview> = [
        {
            title: 'STT',
            key: 'stt',
            width: 45,
            align: 'center',
            render: (_, __, index) => index + 1,
        },
        {
            title: 'Mã HS',
            dataIndex: 'studentCode',
            key: 'studentCode',
            width: 105,
            align: 'center',
            render: (val) => <span style={{ fontFamily: 'Consolas, monospace', fontWeight: 600 }}>{val || '-'}</span>,
        },
        {
            title: 'Họ và tên',
            key: 'fullName',
            width: 160,
            render: (_, record) => <span style={{ fontWeight: 600 }}>{record.fullName || record.studentName}</span>,
        },
        {
            title: 'Khối thu',
            dataIndex: 'revenueBlockName',
            key: 'revenueBlockName',
            width: 100,
            align: 'center',
            render: (val) => val || '-',
        },
        {
            title: 'Chốt thực tế (Suất - Tháng)',
            children: [
                {
                    title: 'Lệch ngày',
                    key: 'rateUnitsDiff',
                    width: 75,
                    align: 'right',
                    render: (_, record) => {
                        const totalUnits = (record.rateItems || []).reduce((acc, it) => acc + (it.units || 0), 0);
                        if (totalUnits === 0) return <span className="text-neutral">-</span>;
                        if (totalUnits > 0) return <span className="text-excess">+{totalUnits}</span>;
                        return <span className="text-debt">{totalUnits}</span>;
                    },
                },
                {
                    title: 'Tiền lệch suất',
                    key: 'rateAmountDiff',
                    width: 110,
                    align: 'right',
                    render: (_, record) => {
                        const totalAmount = (record.rateItems || []).reduce((acc, it) => acc + (it.amount || 0), 0);
                        if (totalAmount === 0) return <span className="text-neutral">-</span>;
                        const details = (record.rateItems || [])
                            .map((it) => `${it.incomeSchoolName}: ${it.amount > 0 ? '+' : ''}${Number(it.amount).toLocaleString('vi-VN')} đ`)
                            .join('; ');
                        if (totalAmount > 0) {
                            return (
                                <Tooltip title={details || undefined}>
                                    <span className="text-excess">+{Number(totalAmount).toLocaleString('vi-VN')}</span>
                                </Tooltip>
                            );
                        }
                        return (
                            <Tooltip title={details || undefined}>
                                <span className="text-debt">{Number(totalAmount).toLocaleString('vi-VN')}</span>
                            </Tooltip>
                        );
                    },
                },
            ],
        },
        {
            title: 'Khoản theo tháng',
            children: [
                {
                    title: 'Nợ chưa đóng',
                    key: 'monthlyDebt',
                    width: 110,
                    align: 'right',
                    render: (_, record) => {
                        const unpaidCost = (record.monthlyItems || []).reduce((acc, it) => acc + (it.amount < 0 ? Math.abs(it.amount) : 0), 0);
                        if (unpaidCost === 0) return <span className="text-neutral">-</span>;
                        const details = (record.monthlyItems || [])
                            .filter((it) => it.amount < 0)
                            .map((it) => `${it.incomeSchoolName}: ${Number(Math.abs(it.amount)).toLocaleString('vi-VN')} đ`)
                            .join('; ');
                        return (
                            <Tooltip title={details || undefined}>
                                <span className="text-debt">{Number(unpaidCost).toLocaleString('vi-VN')}</span>
                            </Tooltip>
                        );
                    },
                },
            ],
        },
        {
            title: `KẾT CHUYỂN SỐ DƯ (THÁNG & SUẤT)`,
            children: [
                {
                    title: 'Tiền thừa (+)',
                    key: 'netCarryExcess',
                    width: 115,
                    align: 'right',
                    render: (_, record) => {
                        if (record.netCarryAmount > 0) {
                            return <span className="text-excess">+{Number(record.netCarryAmount).toLocaleString('vi-VN')}</span>;
                        }
                        return <span className="text-neutral">-</span>;
                    },
                },
                {
                    title: 'Tiền nợ (-)',
                    key: 'netCarryDebt',
                    width: 115,
                    align: 'right',
                    render: (_, record) => {
                        if (record.netCarryAmount < 0) {
                            return <span className="text-debt">{Number(Math.abs(record.netCarryAmount)).toLocaleString('vi-VN')}</span>;
                        }
                        return <span className="text-neutral">-</span>;
                    },
                },
            ],
        },
        {
            title: 'KHOẢN NĂM / KỲ (THU 1 LẦN/NĂM)',
            children: [
                {
                    title: 'Tình trạng năm',
                    key: 'annualStatus',
                    width: 130,
                    align: 'center',
                    render: (_, record) => {
                        if (!record.annualItems || record.annualItems.length === 0) {
                            return <span className="text-neutral">-</span>;
                        }
                        const allPaid = record.annualItems.every((it) => it.isPaid);
                        const tooltipContent = (
                            <div>
                                {record.annualItems.map((it, idx) => (
                                    <div key={idx} style={{ marginBottom: 4 }}>
                                        <strong>{it.incomeSchoolName}</strong>: {it.isPaid ? 'Đã thanh toán cả năm' : `Chưa nộp (${Number(it.amount || 0).toLocaleString('vi-VN')} đ)`}
                                    </div>
                                ))}
                            </div>
                        );
                        if (allPaid) {
                            return (
                                <Tooltip title={tooltipContent}>
                                    <Tag color="success" style={{ fontWeight: 600, cursor: 'help' }}>
                                        ✓ Hoàn thành
                                    </Tag>
                                </Tooltip>
                            );
                        }
                        const hasUnpaid = record.annualItems.some((it) => !it.isPaid);
                        if (hasUnpaid) {
                            return (
                                <Tooltip title={tooltipContent}>
                                    <Tag color="warning" style={{ fontWeight: 600, cursor: 'help' }}>
                                        Chưa nộp
                                    </Tag>
                                </Tooltip>
                            );
                        }
                        return <span className="text-neutral">Chưa ĐK</span>;
                    },
                },
                {
                    title: `Thu lại ở T${nextMonth}`,
                    key: 'annualRecreate',
                    width: 125,
                    align: 'right',
                    render: (_, record) => {
                        const recAmount = record.annualRecreateAmount ?? (record.annualItems || []).reduce((acc, it) => acc + (!it.isPaid ? (it.amount || 0) : 0), 0);
                        if (recAmount > 0) {
                            const details = (record.annualItems || [])
                                .filter((it) => !it.isPaid)
                                .map((it) => `${it.incomeSchoolName}: ${Number(it.amount || 0).toLocaleString('vi-VN')} đ (Hủy bill cũ, tạo lại ở T${nextMonth})`)
                                .join('; ');
                            return (
                                <Tooltip title={details || undefined}>
                                    <span style={{ color: '#722ed1', fontWeight: 700, cursor: 'help' }}>
                                        {Number(recAmount).toLocaleString('vi-VN')}
                                    </span>
                                </Tooltip>
                            );
                        }
                        return <span className="text-neutral">-</span>;
                    },
                },
            ],
        },
        {
            title: 'Biên lai cũ hủy',
            key: 'invoicesToCancel',
            width: 130,
            align: 'center',
            render: (_, record) => {
                if (!record.invoiceIdsToCancel || record.invoiceIdsToCancel.length === 0) {
                    return <span className="text-neutral">-</span>;
                }
                const details = (record.cancelInvoicesDetail && record.cancelInvoicesDetail.length > 0) ? (
                    <div style={{ maxWidth: 320 }}>
                        <div style={{ fontWeight: 700, marginBottom: 6, borderBottom: '1px solid rgba(255,255,255,0.3)', paddingBottom: 2 }}>
                            Danh sách biên lai sẽ tự động hủy:
                        </div>
                        {record.cancelInvoicesDetail.map((inv, idx) => (
                            <div key={idx} style={{ fontSize: 12, marginBottom: 5 }}>
                                <div>
                                    <span style={{ fontFamily: 'Consolas, monospace', fontWeight: 700, color: '#ffd666' }}>{inv.billId}</span>: {inv.incomeSchoolName} (<strong>{Number(inv.cost || 0).toLocaleString('vi-VN')} đ</strong>)
                                </div>
                                {inv.cancelReason && <div style={{ color: '#d9d9d9', fontSize: 11, paddingLeft: 8 }}>└ {inv.cancelReason}</div>}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div>{record.invoiceIdsToCancel.join(', ')}</div>
                );
                return (
                    <Tooltip title={details} placement="left">
                        <Tag color="volcano" style={{ cursor: 'help', fontWeight: 700 }}>
                            {record.invoiceIdsToCancel.length} BL cũ
                        </Tag>
                    </Tooltip>
                );
            },
        },
    ];

    // Tính toán số liệu tổng cộng cho dòng chân bảng (Footer Summary)
    const summaryCalculations = useMemo(() => {
        if (!overviewData || !overviewData.students) {
            return {
                totalRateAmount: 0,
                totalMonthlyDebt: 0,
                totalExcess: 0,
                totalDebt: 0,
                totalAnnualRecreate: 0,
                totalCancelCount: 0,
            };
        }
        let totalRateAmount = 0;
        let totalMonthlyDebt = 0;
        let totalExcess = 0;
        let totalDebt = 0;
        let totalAnnualRecreate = 0;
        let totalCancelCount = 0;

        overviewData.students.forEach((s) => {
            const rateAmt = (s.rateItems || []).reduce((acc, it) => acc + (it.amount || 0), 0);
            totalRateAmount += rateAmt;

            const mDebt = (s.monthlyItems || []).reduce((acc, it) => acc + (it.amount < 0 ? Math.abs(it.amount) : 0), 0);
            totalMonthlyDebt += mDebt;

            if (s.netCarryAmount > 0) totalExcess += s.netCarryAmount;
            if (s.netCarryAmount < 0) totalDebt += Math.abs(s.netCarryAmount);

            const sAnn = s.annualRecreateAmount ?? (s.annualItems || []).reduce((acc, it) => acc + (!it.isPaid ? (it.amount || 0) : 0), 0);
            totalAnnualRecreate += sAnn;

            totalCancelCount += (s.invoiceIdsToCancel || []).length;
        });

        return {
            totalRateAmount,
            totalMonthlyDebt,
            totalExcess,
            totalDebt,
            totalAnnualRecreate,
            totalCancelCount,
        };
    }, [overviewData]);

    return (
        <div className="carry-forward-page">
            {/* Filter Card */}
            <Card className="filter-card" bodyStyle={{ padding: '12px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                    {/* Left: Filters */}
                    <Space wrap size={10} align="center">
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate(`/class-actual-settle?classYearId=${selectedClassYearId || ''}`)}
                        >
                            Về Chốt thực tế
                        </Button>

                        <Divider type="vertical" style={{ height: 24 }} />

                        <ClassLevelSelect
                            value={selectedClassLevel}
                            onChange={handleClassLevelChange}
                            placeholder="Khối lớp"
                            style={{ width: 130 }}
                        />

                        <Select
                            placeholder="Chọn lớp học"
                            value={selectedClassYearId ?? undefined}
                            onChange={handleClassYearChange}
                            style={{ width: 220 }}
                            showSearch
                            optionFilterProp="children"
                        >
                            {filteredClassList.map((c) => (
                                <Option key={c.classYearId} value={c.classYearId}>
                                    {c.className} ({c.schoolYear})
                                </Option>
                            ))}
                        </Select>

                        <Select
                            value={selectedMonth}
                            onChange={(m) => setSelectedMonth(m)}
                            style={{ width: 105 }}
                        >
                            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                                <Option key={m} value={m}>
                                    Tháng {m}
                                </Option>
                            ))}
                        </Select>

                        {selectedYear && (
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#0958d9' }}>
                                <CalendarOutlined style={{ marginRight: 4 }} />
                                Năm {selectedYear.year()}
                            </span>
                        )}

                        <Tooltip title="Tải lại dữ liệu">
                            <Button icon={<ReloadOutlined />} onClick={fetchOverviewData} loading={loading} />
                        </Tooltip>
                    </Space>

                    {/* Right: Actions */}
                    <Space size={12} align="center">
                        <Button
                            type="primary"
                            icon={<SwapRightOutlined />}
                            disabled={overviewData?.settleStatus !== 1 || !overviewData || overviewData.students.length === 0}
                            onClick={() => setShowCarryConfirm(true)}
                            style={{
                                backgroundColor: overviewData?.settleStatus === 1 ? '#d46b08' : undefined,
                                borderColor: overviewData?.settleStatus === 1 ? '#d46b08' : undefined,
                                fontWeight: 700,
                                borderRadius: 4,
                            }}
                        >
                            {overviewData?.settleStatus === 2 ? 'Đã kết chuyển & Khóa sổ' : 'Thực hiện Kết chuyển & Khóa sổ'}
                        </Button>
                    </Space>
                </div>

                {/* Settle Status Alerts */}
                {overviewData && (
                    <div style={{ marginTop: 10 }}>
                        {overviewData.settleStatus === 0 && (
                            <Alert
                                type="warning"
                                showIcon
                                icon={<ExclamationCircleOutlined />}
                                style={{ padding: '6px 12px', fontSize: 13 }}
                                message={
                                    <span>
                                        <strong>Chưa chốt thực tế:</strong> Tháng {selectedMonth}/{selectedYear?.year()} chưa được chốt thực tế. Bạn phải chốt thực tế trước khi kết chuyển số dư.{' '}
                                        <Button
                                            type="link"
                                            size="small"
                                            onClick={() => navigate(`/class-actual-settle?classYearId=${selectedClassYearId || ''}`)}
                                            style={{ padding: 0, fontWeight: 600 }}
                                        >
                                            Sang màn hình Chốt thực tế ngay &rarr;
                                        </Button>
                                    </span>
                                }
                            />
                        )}
                        {overviewData.settleStatus === 1 && (
                            <Alert
                                type="success"
                                showIcon
                                icon={<CheckCircleOutlined />}
                                style={{ padding: '6px 12px', fontSize: 13 }}
                                message={
                                    <span>
                                        <strong>Đã chốt thực tế Tháng {selectedMonth}/{selectedYear?.year()}:</strong> Dữ liệu đã sẵn sàng để kết chuyển sang <strong>Tháng {nextMonth}/{nextYear}</strong>. Khi bấm [Thực hiện Kết chuyển], các biên lai chưa đóng cũ sẽ được tự động hủy để tránh trùng lặp.
                                    </span>
                                }
                            />
                        )}
                        {overviewData.settleStatus === 2 && (
                            <Alert
                                type="info"
                                showIcon
                                icon={<LockOutlined />}
                                style={{ padding: '6px 12px', fontSize: 13 }}
                                message={
                                    <span>
                                        <strong>Tháng này đã được kết chuyển & khóa sổ:</strong> Đã chuyển toàn bộ thừa/nợ sang Tháng {nextMonth}/{nextYear}.
                                    </span>
                                }
                            />
                        )}
                    </div>
                )}
            </Card>

            {/* 5 Chỉ tiêu tài chính tổng hợp */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: 12,
                    marginBottom: 14,
                }}
            >
                <Card className="stat-card stat-students" bodyStyle={{ padding: '10px 14px' }}>
                    <div className="stat-title">Tổng học sinh</div>
                    <div className="stat-value" style={{ color: '#0958d9' }}>
                        {overviewData?.totalStudents ?? 0}
                    </div>
                </Card>

                <Card className="stat-card stat-excess" bodyStyle={{ padding: '10px 14px' }}>
                    <div className="stat-title">Tiền thừa sang T{nextMonth} (+)</div>
                    <div className="stat-value text-excess">
                        +{Number(overviewData?.totalExcessAmount || 0).toLocaleString('vi-VN')} đ
                    </div>
                </Card>

                <Card className="stat-card stat-debt" bodyStyle={{ padding: '10px 14px' }}>
                    <div className="stat-title">Tiền nợ sang T{nextMonth} (-)</div>
                    <div className="stat-value text-debt">
                        -{Number(overviewData?.totalDebtAmount || 0).toLocaleString('vi-VN')} đ
                    </div>
                </Card>

                <Card className="stat-card stat-annual" bodyStyle={{ padding: '10px 14px' }}>
                    <div className="stat-title">Khoản năm tạo lại ở T{nextMonth}</div>
                    <div className="stat-value" style={{ color: '#722ed1' }}>
                        {Number(overviewData?.totalAnnualUnpaidAmount ?? summaryCalculations.totalAnnualRecreate).toLocaleString('vi-VN')} đ
                    </div>
                </Card>

                <Card className="stat-card stat-cancel" bodyStyle={{ padding: '10px 14px' }}>
                    <div className="stat-title">Biên lai cũ sẽ hủy</div>
                    <div className="stat-value" style={{ color: '#d46b08' }}>
                        {overviewData?.totalInvoicesToCancel ?? 0} <span style={{ fontSize: 13, fontWeight: 500, color: '#8c8c8c' }}>BL</span>
                    </div>
                </Card>
            </div>

            {/* Main Accounting Table */}
            <Card
                className="accounting-table-card"
                title={
                    <Space size={8}>
                        <FileTextOutlined style={{ color: '#0958d9' }} />
                        <span style={{ fontWeight: 700, fontSize: 14 }}>
                            BẢNG ĐỐI SOÁT & KẾT CHUYỂN SỐ DƯ SANG THÁNG {nextMonth}/{nextYear}
                        </span>
                    </Space>
                }
                bodyStyle={{ padding: 0 }}
            >
                <Table
                    className="accounting-table"
                    bordered
                    columns={columns}
                    dataSource={overviewData?.students || []}
                    rowKey="studentClassYearId"
                    loading={loading}
                    pagination={{
                        pageSize: 50,
                        showSizeChanger: true,
                        pageSizeOptions: ['20', '50', '100'],
                        showTotal: (total) => `Tổng cộng ${total} học sinh`,
                    }}
                    size="small"
                    scroll={{ x: 1200 }}
                    summary={() => {
                        const { totalRateAmount, totalMonthlyDebt, totalExcess, totalDebt, totalAnnualRecreate, totalCancelCount } = summaryCalculations;
                        return (
                            <Table.Summary fixed>
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0} colSpan={4} align="center">
                                        <strong>TỔNG CỘNG ({overviewData?.totalStudents ?? 0} học sinh)</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={4} align="center">
                                        -
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={5} align="right">
                                        {totalRateAmount !== 0 ? (
                                            <span className={totalRateAmount > 0 ? 'text-excess' : 'text-debt'}>
                                                {totalRateAmount > 0 ? '+' : ''}{Number(totalRateAmount).toLocaleString('vi-VN')}
                                            </span>
                                        ) : '-'}
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={6} align="right">
                                        {totalMonthlyDebt > 0 ? (
                                            <span className="text-debt">{Number(totalMonthlyDebt).toLocaleString('vi-VN')}</span>
                                        ) : '-'}
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={7} align="right">
                                        <span className="text-excess">
                                            +{Number(totalExcess).toLocaleString('vi-VN')}
                                        </span>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={8} align="right">
                                        <span className="text-debt">
                                            {Number(totalDebt).toLocaleString('vi-VN')}
                                        </span>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={9} align="center">
                                        -
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={10} align="right">
                                        {totalAnnualRecreate > 0 ? (
                                            <span style={{ color: '#722ed1', fontWeight: 700 }}>
                                                {Number(totalAnnualRecreate).toLocaleString('vi-VN')}
                                            </span>
                                        ) : '-'}
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={11} align="center">
                                        <span className="text-cancel-bill" style={{ fontWeight: 700 }}>
                                            {totalCancelCount} BL
                                        </span>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            </Table.Summary>
                        );
                    }}
                />
            </Card>

            {/* Modal Xác nhận Kết chuyển */}
            <Modal
                title={
                    <Space size={8}>
                        <ExclamationCircleOutlined style={{ color: '#d46b08', fontSize: 20 }} />
                        <span>Xác nhận Kết chuyển số dư sang Tháng {nextMonth}/{nextYear}</span>
                    </Space>
                }
                open={showCarryConfirm}
                onCancel={() => setShowCarryConfirm(false)}
                footer={[
                    <Button key="back" onClick={() => setShowCarryConfirm(false)}>
                        Đóng
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={carrying}
                        onClick={handleExecuteCarryForward}
                        style={{ backgroundColor: '#d46b08', borderColor: '#d46b08', fontWeight: 700 }}
                    >
                        Xác nhận Kết chuyển & Khóa sổ
                    </Button>,
                ]}
                width={580}
            >
                <div style={{ padding: '8px 0' }}>
                    <Alert
                        type="warning"
                        showIcon
                        message="Hành động này sẽ thực hiện các thao tác tự động sau:"
                        description={
                            <ul style={{ paddingLeft: 18, marginTop: 8, marginBottom: 4 }}>
                                <li>
                                    <strong>Chuyển số dư thừa/nợ (tháng & suất):</strong> Kết chuyển số dư của toàn bộ học sinh sang cột carry của <strong>Tháng {nextMonth}/{nextYear}</strong>.
                                </li>
                                <li>
                                    <strong>Hủy biên lai cũ chưa thanh toán ({overviewData?.totalInvoicesToCancel ?? 0} biên lai):</strong> Tự động gạch hủy để phụ huynh không quét nhầm QR cũ hoặc trùng lặp hóa đơn.
                                </li>
                                <li>
                                    <strong>Khoản thu theo năm / kỳ (BHYT, CSVC...):</strong>
                                    <ul style={{ paddingLeft: 16, marginTop: 4 }}>
                                        <li>Học sinh <em>đã nộp</em> trong năm học: Hoàn tất cả năm, tháng sau <strong>không tạo lại</strong>.</li>
                                        <li>Học sinh <em>chưa nộp</em>: Hủy biên lai tháng cũ và <strong>tự động tạo lại đăng ký ở Tháng {nextMonth}/{nextYear}</strong> để xuất biên lai mới.</li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Khóa sổ Tháng {selectedMonth}/{selectedYear?.year()}:</strong> Đóng kỳ kế toán để tránh sửa đổi dữ liệu sau kết chuyển.
                                </li>
                            </ul>
                        }
                    />

                    <div style={{ marginTop: 16, background: '#fafafa', padding: 12, borderRadius: 6, border: '1px solid #e8e8e8' }}>
                        <Checkbox
                            checked={copyQuickRegister}
                            onChange={(e) => setCopyQuickRegister(e.target.checked)}
                        >
                            <span style={{ fontWeight: 600 }}>
                                Sao chép khuôn mẫu Đăng ký nhanh (Quick Register) sang Tháng {nextMonth}/{nextYear}
                            </span>
                        </Checkbox>
                        <div style={{ fontSize: 12, color: '#595959', marginTop: 4, paddingLeft: 24 }}>
                            Tự động sao chép danh sách nguồn thu mặc định của từng khối thu sang tháng mới (riêng các khoản theo năm đã đóng sẽ tự động loại trừ).
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default ClassCarryForwardBoard;
